import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { StatusBadge } from '../components/common/StatusBadge';
import { PriorityBadge } from '../components/common/PriorityBadge';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  TicketIcon,
  ClockIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline';

interface Ticket {
  id: string;
  ticketNumber: string;
  subject: string;
  description?: string;
  status: string;
  priority: string;
  assignedTo?: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
  };
  escalatedBy?: {
    firstName: string;
    lastName: string;
  };
  escalatedAt?: string;
  escalationReason?: string;
  createdAt: string;
  telegramUsername?: string;
  telegramFirstName?: string;
  telegramLastName?: string;
}

const SecondLinePage: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTickets();
  }, [searchTerm, statusFilter, currentPage]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await api.get(`/api/tickets/second-line?${params.toString()}`);
      
      if (response.data.success) {
        const apiData = response.data.data || {};
        setTickets(apiData.tickets || []);
        setTotalPages(apiData.pagination?.pages || 1);
      }
    } catch (error: any) {
      if (error.response?.status === 403) {
        toast.error('Нет доступа к заявкам 2-й линии');
      } else {
        toast.error('Ошибка загрузки заявок 2-й линии');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString?: string) => {
    if (!dateString) return 'Не указано';
    try {
      return new Date(dateString).toLocaleString('ru-RU');
    } catch {
      return 'Не указано';
    }
  };

  const getTimeInQueue = (escalatedAt?: string) => {
    if (!escalatedAt) return 'Не указано';
    try {
      const escalated = new Date(escalatedAt).getTime();
      const now = Date.now();
      const diff = Math.floor((now - escalated) / 1000); // секунды
      
      if (diff < 60) return `${diff} сек`;
      if (diff < 3600) return `${Math.floor(diff / 60)} мин`;
      if (diff < 86400) return `${Math.floor(diff / 3600)} ч`;
      return `${Math.floor(diff / 86400)} дн`;
    } catch {
      return 'Не указано';
    }
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Загрузка заявок 2-й линии...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <ArrowUpIcon className="h-6 w-6" />
          Заявки 2-й линии поддержки
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Эскалированные заявки, требующие специализированной поддержки
        </p>
      </div>

      {/* Фильтры */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Поиск по номеру, теме или клиенту..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-border rounded-md bg-background text-foreground"
            >
              <option value="">Все статусы</option>
              <option value="new">Новая</option>
              <option value="assigned">Назначена</option>
              <option value="in_progress">В работе</option>
              <option value="paused">Приостановлена</option>
              <option value="resolved">Решена</option>
              <option value="closed">Закрыта</option>
            </select>
          </div>
        </div>
      </div>

      {/* Список заявок */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {tickets.length === 0 ? (
          <div className="p-12 text-center">
            <TicketIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Нет заявок 2-й линии</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Номер</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Тема</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Клиент</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Статус</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Приоритет</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Эскалировано</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">В очереди</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tickets.map((ticket) => (
                  <tr key={ticket.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <Link
                        to={`/tickets/${ticket.id}`}
                        className="text-primary hover:underline font-medium"
                      >
                        {ticket.ticketNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-xs truncate" title={ticket.subject}>
                        {ticket.subject}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        {ticket.telegramUsername 
                          ? `@${ticket.telegramUsername}`
                          : `${ticket.telegramFirstName || ''} ${ticket.telegramLastName || ''}`.trim() || 'Клиент'
                        }
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={ticket.status} />
                    </td>
                    <td className="px-4 py-3">
                      <PriorityBadge priority={ticket.priority} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        {ticket.escalatedBy 
                          ? `${ticket.escalatedBy.firstName} ${ticket.escalatedBy.lastName}`
                          : 'Не указано'
                        }
                        {ticket.escalationReason && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {ticket.escalationReason}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <ClockIcon className="h-4 w-4" />
                        {getTimeInQueue(ticket.escalatedAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/tickets/${ticket.id}`)}
                        >
                          Открыть
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Пагинация */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-border flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Страница {currentPage} из {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                Первая
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Назад
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Вперед
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Последняя
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecondLinePage;

